import React from 'react';
import PropTypes from 'prop-types';
import '../../../styles/attendees/imports/main.scss';

import invariant from 'invariant';
import { fetcher } from '@weezevent/weezjs-core';
import { Message, WeezHeader, Card } from '@weezevent/nacre';
import { useTranslation, Trans } from 'react-i18next';

import Distributor from '../../../models/distributor';

import { Link } from 'react-router-dom';

import ImportProcess from './model';
import { Icon, Loader, Select } from 'semantic-ui-react';
import { ImportController } from '../../../nacre/importController';
import Inventory from '../../../models/inventory';
import Loading from '../../../components/loading';

const authenticatedFetch = fetcher.request.bind(fetcher);

const SocialDistancingMessage = ({ organizationId, eventId }) => {
    const { t } = useTranslation();

    return (
        <Message title={t('ticket.attendees.import.statusSocialDistancing')} type="default">
            {t('ticket.attendees.import.infoSocialDistancing')}{' '}
            <Link target="_blank" className="go-to-participants" to={`/ticket/O${organizationId}/E${eventId}/manager/attendees`}>
                {t('ticket.attendees.import.labelLinkManageAttendees')}
            </Link>
        </Message>
    );
};

SocialDistancingMessage.propTypes = {
    organizationId: PropTypes.string.isRequired,
    eventId: PropTypes.string.isRequired
};

const DistributorSelector = ({ selectedDistributor = null, distributors, onChange }) => {
    let options = distributors.map(d => ({ text: d.label, value: d.id }));
    const { t } = useTranslation();

    return (
        <Card.FormStep title={t('ticket.attendees.import.distributor_title')} subtitle={t('ticket.attendees.import.distributor_subtitle')}>
            <div>
                <Select clearable fluid disabled={!distributors.length} value={selectedDistributor} options={options} onChange={(synth, { value }) => onChange(value)} />
            </div>
        </Card.FormStep>
    );
};

DistributorSelector.propTypes = {
    selectedDistributor: PropTypes.number,
    distributors: PropTypes.array.isRequired,
    onChange: PropTypes.func.isRequired
};

/** WillyImportController is a layer above ImportController that
 * handles the synchronization with the Gofast API.
 */
function WillyImportController({ baseURL, importID, query = null, ...props }) {
    const [importProcess, setInstance] = React.useState(null);

    const createImportProcess = React.useCallback(
        async data => {
            invariant(data, 'data is required');

            if (query) {
                data = {
                    ...data,
                    ...query
                };
            }

            const { response } = await fetcher.post(baseURL, { data });
            setInstance(new ImportProcess(response));
        },
        [baseURL, query]
    );
    const getImportProcess = React.useCallback(
        async id => {
            invariant(id, 'id is required');
            const endpoint = `${baseURL}/${id}`;
            const { response } = await fetcher.get(endpoint, { cache: false });
            setInstance(new ImportProcess(response));
        },
        [baseURL]
    );
    const triggerImportProcess = React.useCallback(
        async (id, data) => {
            invariant(id, 'id is required');
            const endpoint = `${baseURL}/${id}`;
            const { response } = await fetcher.patch(endpoint, { data });
            setInstance(new ImportProcess(response));
        },
        [baseURL]
    );
    const onCancel = React.useCallback(() => {
        if (!importID) {
            setInstance(null);
        }
    }, [importID]);

    React.useEffect(() => {
        if (importID) {
            getImportProcess(importID);
        }
    }, []);

    if (importID && !importProcess) {
        return <Loader />;
    }

    return (
        <ImportController
            onCancel={onCancel}
            createImportProcess={createImportProcess}
            refreshImportProcess={getImportProcess}
            triggerImportProcess={triggerImportProcess}
            importProcess={importProcess}
            fetch={authenticatedFetch}
            {...props}
        />
    );
}

WillyImportController.propTypes = {
    baseURL: PropTypes.string.isRequired,
    importID: PropTypes.string,
    query: PropTypes.object
};

/** Controller for the import process of attendees.
 *
 * It leverages WillyImportController to synchronize with the API and
 * ImportController to communicate with the Gofast API.
 */
export default function AttendeeImportController({ organization, event, ...props }) {
    const { t } = useTranslation();
    const organizationId = organization.id;
    const eventId = event.id;

    // Gofast API URL
    const baseURL = `/ticket/organizations/${organizationId}/events/${eventId}/orders/import/import`;
    // S3 authorization URL
    const authorizationURL = `/ticket/organizations/${organizationId}/events/${eventId}/orders/import/config`;

    let [distributors, setDistributors] = React.useState(null);
    let [selectedDistributor, setSelectedDistributor] = React.useState(null);
    let [isSocialDistancing, setIsSocialDistancing] = React.useState(false);
    let [loading, setLoading] = React.useState(true);

    React.useEffect(() => {
        let distributorsPromise = Distributor.getAll({ organizationId, eventId });
        let inventoriesPromise = Inventory.forEvent({ organizationId: organizationId, eventId: eventId });

        Promise.all([distributorsPromise, inventoriesPromise])
            .then(([distributorsResponse, inventoriesPromiseResponse]) => {
                if (distributorsResponse) {
                    setDistributors(distributorsResponse);
                }
                if (inventoriesPromiseResponse) {
                    if (inventoriesPromiseResponse.some(inventory => inventory.social_distancing_x + inventory.social_distancing_y + inventory.social_distancing_z > 0)) {
                        setIsSocialDistancing(true);
                    }
                }
                setLoading(false);
            })
            .catch(e => {});
    }, [organizationId, eventId]);

    if (loading) {
        return <Loading />;
    }

    return (
        <React.Fragment>
            <WeezHeader title={t('ticket.attendees.import.title')} />
            {isSocialDistancing && <SocialDistancingMessage organizationId={organizationId} eventId={eventId} />}
            {distributors && <DistributorSelector distributors={distributors} selectedDistributor={selectedDistributor} onChange={setSelectedDistributor} />}
            <Message type="default" title={t('ticket.attendees.import.exclude_fees_from_price.title')}>
                {t('ticket.attendees.import.exclude_fees_from_price.description')}
            </Message>
            <WillyImportController
                importProcessSlug="orders"
                authorizationURL={authorizationURL}
                baseURL={baseURL}
                query={{ distributor_id: selectedDistributor }}
                titles={{
                    initial: t('ticket.attendees.import.step.title.initial'),
                    'file-uploading': t('ticket.attendees.import.step.title.initial'),
                    'import-process-validating': t('ticket.attendees.import.step.title.import-process-validating-confirm'),
                    'import-process-confirm': t('ticket.attendees.import.step.title.import-process-validating-confirm'),
                    'import-process-processing': t('ticket.attendees.import.step.title.import-process-processing-done'),
                    'import-process-done': t('ticket.attendees.import.step.title.import-process-processing-done')
                }}
                subtitles={{
                    initial: (
                        <React.Fragment>
                            <div>{t('ticket.attendees.import.step.info.initial')}</div>
                            <div>
                                {t('ticket.attendees.import.step.info.initial_1')}
                                <a className="link" href="/ticket/static/template_import_weezevent.xlsx" download>
                                    {t('ticket.attendees.import.step.info.fichier')}
                                </a>{' '}
                                {t('ticket.attendees.import.step.info.initial_2')}
                            </div>
                        </React.Fragment>
                    ),
                    'file-uploading': t('ticket.attendees.import.step.info.import-process-validating'),
                    'import-process-validating': t('ticket.attendees.import.step.info.import-process-validating'),
                    'import-process-confirm': t('ticket.attendees.import.step.info.import-process-confirm'),
                    'import-process-processing': t('ticket.attendees.import.step.info.import-process-processing'),
                    'import-process-done': t('ticket.attendees.import.step.info.import-process-done')
                }}
                abortWarning={t('ticket.attendees.import.error_leaving')}
                validationError={ValidationError}
                results={AttendeeImportResults}
                {...props}
            />
        </React.Fragment>
    );
}

AttendeeImportController.propTypes = {
    organization: PropTypes.object.isRequired,
    event: PropTypes.object.isRequired
};

function AttendeeImportResults({ importProcess }) {
    if (!importProcess) {
        return null;
    }

    const createdCount = importProcess.countCreatedRows();
    const errorCount = importProcess.countErrorRows();

    return (
        <>
            <p>
                <Icon className="icon-weez" name="tasks" />
                <span className="text-details">
                    <Trans i18nKey="ticket.attendees.import.table_error.summary.import_line" values={{ count: createdCount }} count={createdCount}>
                        %createdCount% Imported attendees
                    </Trans>
                </span>
            </p>
            <p>
                <Icon className="icon-error" name="times circle outline" />
                <span className="text-details">
                    <Trans i18nKey="ticket.attendees.import.table_error.summary.invalid_attendee" values={{ count: errorCount }} count={errorCount}>
                        %errorCount% Invalid attendees
                    </Trans>
                </span>
            </p>
        </>
    );
}

AttendeeImportResults.propTypes = {
    importProcess: PropTypes.instanceOf(ImportProcess).isRequired
};

function ValidationError({ name }) {
    const [prefix, value] = name.split(':');
    const trad = `ticket.attendees.import.error.${prefix}`;
    return <Trans i18nKey={trad} values={{ value }} />;
}

ValidationError.propTypes = {
    name: PropTypes.string.isRequired
};
